import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../../src/theme/useTheme';
import {
  useApiClient,
  extractErrorMessage,
  isRateLimitError,
  isTransientServerError,
} from '../../../helpers/apiClient.helper';
import { useDialog } from '../../../src/shared/hooks/useDialog';
import { useSnackbar } from '../../../src/shared/hooks/useSnackbar';
import { sanitizeInput } from '../../../src/helpers/sanitizeInput.helper';
import { useCompetitionId, useCompetitionBasePath } from '../../../src/competitions/contexts/CompetitionContext';
import { competitionGroupsRoute, competitionGroupRoute } from '../../../src/competitions/helpers/competitionRoutes.helper';
import { joinWorldCupCompetitionGroup } from '../../../src/worldcup/helpers/worldCupCompetitionGroupsApi';
import { worldCupQueryKeys } from '../../../src/worldcup/constants/worldcupQueryKeys';
import { clearPendingWcSquadJoinCode } from '../../../src/join/helpers/pendingWcSquadJoin.helper';
import {
  fetchPublicSquadByCode,
  type PublicSquadLookupResponse,
} from '../../../src/onboarding/helpers/onboardingPublicApi';
import { TicketStub } from '../../onboarding/TicketStub/TicketStub';
import { getLocksPastLabel } from '../../onboarding/TicketStub/ticketStubLockIn.helper';
import type { TicketStubBrand } from '../../onboarding/TicketStub/TicketStub.types';
import { TAB_BAR_HEIGHT } from '../../../appStyles/_layout.styles';
import { getJoinWorldCupGroupScreenStyles } from './JoinWorldCupGroupScreen.styles';
import {
  getPreviewSquadSubtitleFallback,
  JOIN_WC_GROUP_COPY,
  JOIN_WC_INVITE_CODE_LENGTH,
  JOIN_WC_INVITE_CODE_MAX_LENGTH,
  JOIN_WC_AUTO_JOIN_MAX_RETRIES,
  JOIN_WC_AUTO_JOIN_RETRY_DELAY_MS,
} from './JoinWorldCupGroupScreen.constants';
import { JoinCodeEntry } from './JoinCodeEntry/JoinCodeEntry';

type JoinPhase = 'entering' | 'previewing' | 'preview';

interface JoinWorldCupGroupScreenProps {
  initialCode?: string;
  /**
   * When true and `initialCode` is present, auto-submit the join request on
   * mount. Used by the onboarding waiting-room flow to finalise the join
   * immediately after sign-in / sign-up.
   */
  autoJoin?: boolean;
}

export const JoinWorldCupGroupScreen: React.FC<JoinWorldCupGroupScreenProps> = ({
  initialCode,
  autoJoin = false,
}) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const competitionId = useCompetitionId();
  const basePath = useCompetitionBasePath();
  const styles = useMemo(() => getJoinWorldCupGroupScreenStyles(theme), [theme]);
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { showAlert } = useDialog();
  const { showSnackbar } = useSnackbar();

  const [phase, setPhase] = useState<JoinPhase>('entering');
  const [code, setCode] = useState('');
  const [squadPreview, setSquadPreview] = useState<PublicSquadLookupResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [ticketKey, setTicketKey] = useState(0);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoJoinFailed, setAutoJoinFailed] = useState(false);

  const goToWorldCupGroupsTab = useCallback(() => {
    router.replace(competitionGroupsRoute(basePath) as never);
  }, [router]);

  // This screen is the authoritative handler for an invite (it owns the join via
  // the URL `code`). Relinquish the durable pending-join fallback so the app-shell
  // usePendingWcSquadJoinHandler doesn't fire a redundant concurrent join — which
  // could win the race and surface a spurious "Already a member" error here.
  useEffect(() => {
    void clearPendingWcSquadJoinCode();
  }, []);

  const performJoin = useCallback(
    async (codeToJoin: string, opts?: { retryOnTransient?: boolean }): Promise<boolean> => {
      setIsSubmitting(true);
      const sanitized = sanitizeInput(codeToJoin.trim(), true).trim().toUpperCase();
      const maxRetries = opts?.retryOnTransient ? JOIN_WC_AUTO_JOIN_MAX_RETRIES : 0;
      try {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            const { groupId } = await joinWorldCupCompetitionGroup(apiClient, competitionId, sanitized);
            showSnackbar('Joined squad!', 'success');
            void queryClient.invalidateQueries({
              queryKey: worldCupQueryKeys.groups(competitionId),
            });
            router.replace(competitionGroupRoute(basePath, groupId) as never);
            return true;
          } catch (err) {
            // First-login settling window: a brand-new account (e.g. cross-app OAuth signup) can
            // 5xx while ensureUserExists provisions the user row. Retry once — keeping the loading
            // shell up — before surfacing anything to the user.
            if (attempt < maxRetries && isTransientServerError(err)) {
              await new Promise(resolve => setTimeout(resolve, JOIN_WC_AUTO_JOIN_RETRY_DELAY_MS));
              continue;
            }
            if (!isRateLimitError(err)) {
              await showAlert({
                title: JOIN_WC_GROUP_COPY.ERROR_TITLE,
                message: extractErrorMessage(err, 'Invalid code or squad not found. Try again.'),
                type: 'danger',
              });
            }
            return false;
          }
        }
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [apiClient, queryClient, router, showAlert, showSnackbar],
  );

  const handleJoin = useCallback((): void => {
    void (async () => {
      const success = await performJoin(code);
      if (!success) {
        // Reset ticket so the animation state is fresh for a retry
        setTicketKey(k => k + 1);
      }
    })();
  }, [performJoin, code]);

  const fetchCancelRef = useRef<(() => void) | null>(null);

  const fetchPreview = useCallback(async (inviteCode: string): Promise<void> => {
    fetchCancelRef.current?.();
    let cancelled = false;
    fetchCancelRef.current = () => { cancelled = true; };

    setPhase('previewing');
    setPreviewError(null);
    try {
      // Invite codes are unique per competition, and the preview is the ONLY path to
      // the Join CTA — looking a season-pool code up against the World Cup 404s and
      // leaves the user with no way to join at all.
      const squad = await fetchPublicSquadByCode(competitionId, inviteCode);
      if (cancelled) return;
      setSquadPreview(squad);
      setPhase('preview');
    } catch (err) {
      if (cancelled) return;
      setPreviewError(extractErrorMessage(err, JOIN_WC_GROUP_COPY.PREVIEW_ERROR));
      setPhase('entering');
    }
  }, [competitionId]);

  const handleCodeChange = useCallback(
    (newCode: string) => {
      // If user edits while a preview fetch is in-flight, cancel it and return
      // to the entry phase so they can keep typing without races.
      fetchCancelRef.current?.();
      fetchCancelRef.current = null;
      setCode(newCode);
      setPreviewError(null);
      setPhase('entering');
      if (newCode.length === JOIN_WC_INVITE_CODE_LENGTH) {
        void fetchPreview(newCode);
      }
    },
    [fetchPreview],
  );

  const handleResetToEntering = useCallback(() => {
    setPhase('entering');
    setCode('');
    setSquadPreview(null);
    setPreviewError(null);
  }, []);

  // Pre-fill and auto-preview when arriving via invite link (non-autoJoin path)
  useEffect(() => {
    if (!initialCode || autoJoin) return;
    const clean = sanitizeInput(initialCode.trim(), true)
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, JOIN_WC_INVITE_CODE_LENGTH)
      .toUpperCase();
    setCode(clean);
    if (clean.length === JOIN_WC_INVITE_CODE_LENGTH) {
      void fetchPreview(clean);
    }
    // Run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-join path: skip the ticket UI and join immediately (used after sign-up from invite link)
  const autoJoinCode = useMemo(
    () =>
      sanitizeInput((initialCode ?? '').trim(), true)
        .slice(0, JOIN_WC_INVITE_CODE_MAX_LENGTH)
        .toUpperCase(),
    [initialCode],
  );
  const autoJoinTriggeredRef = useRef(false);
  useEffect(() => {
    if (!autoJoin) return;
    if (autoJoinTriggeredRef.current) return;
    if (!autoJoinCode.trim() || isSubmitting) return;
    autoJoinTriggeredRef.current = true;
    void (async () => {
      const success = await performJoin(autoJoinCode, { retryOnTransient: true });
      if (!success) setAutoJoinFailed(true);
    })();
  }, [autoJoin, autoJoinCode, isSubmitting, performJoin]);

  // Auto-join loading shell — shown until the join resolves or fails
  const showAutoJoinShell = autoJoin && autoJoinCode.trim().length > 0 && !autoJoinFailed;
  if (showAutoJoinShell) {
    return (
      <View style={styles.container} testID="join-world-cup-group-auto-join-shell">
        <View style={styles.autoJoinShell}>
          <ActivityIndicator size="large" color={theme.primary} />
          <Text style={styles.autoJoinShellTitle}>{JOIN_WC_GROUP_COPY.AUTO_JOIN_TITLE}</Text>
          <Text style={styles.autoJoinShellSubtitle}>{JOIN_WC_GROUP_COPY.AUTO_JOIN_SUBTITLE}</Text>
        </View>
      </View>
    );
  }

  // Preview phase — full ticket with tear animation
  if (phase === 'preview' && squadPreview) {
    const companyName = squadPreview.companyBranding?.companyName ?? null;
    const brand: TicketStubBrand = {
      name: companyName ?? squadPreview.name,
      primaryColor: squadPreview.companyBranding?.primaryColor ?? theme.primary,
      secondaryColor: squadPreview.companyBranding?.secondaryColor ?? null,
      logoUrl: squadPreview.companyBranding?.logoUrl ?? squadPreview.customLogoUrl ?? null,
    };
    const formattedCode =
      code.length === JOIN_WC_INVITE_CODE_LENGTH
        ? `${code.slice(0, 3)}-${code.slice(3)}`
        : code;
    const memberSuffix =
      squadPreview.memberCount === 1
        ? JOIN_WC_GROUP_COPY.PREVIEW_MEMBER_COUNT_SUFFIX_ONE
        : JOIN_WC_GROUP_COPY.PREVIEW_MEMBER_COUNT_SUFFIX_MANY;
    return (
      <TicketStub
        key={ticketKey}
        brand={brand}
        presentedBy={
          companyName ? `${JOIN_WC_GROUP_COPY.PREVIEW_PRESENTED_BY}${companyName}` : null
        }
        title={`${JOIN_WC_GROUP_COPY.PREVIEW_TITLE_PREFIX}${squadPreview.name}`}
        subtitle={squadPreview.description?.trim() || getPreviewSquadSubtitleFallback(squadPreview.competition?.name)}
        memberCount={squadPreview.memberCount}
        memberCountLabel={`${squadPreview.memberCount} ${memberSuffix}`}
        groupCode={formattedCode}
        matchCount={squadPreview.competition?.matchCount ?? null}
        locksAtIso={squadPreview.competition?.locksAt ?? null}
        locksPastLabel={getLocksPastLabel(squadPreview.competition?.lifecycle)}
        primaryCta={{ label: JOIN_WC_GROUP_COPY.PREVIEW_JOIN_CTA, onPress: handleJoin }}
        disabledNotice={null}
        secondaryCta={{
          label: JOIN_WC_GROUP_COPY.PREVIEW_DIFFERENT_CODE,
          onPress: handleResetToEntering,
        }}
        showOpenInApp={false}
        contentBottomInset={insets.bottom + TAB_BAR_HEIGHT}
      />
    );
  }

  // Entering / previewing phases — OTP code entry
  return (
    <View style={styles.container}>
      <JoinCodeEntry
        code={code}
        onChange={handleCodeChange}
        onBack={goToWorldCupGroupsTab}
        error={previewError}
        isLoading={phase === 'previewing'}
      />
    </View>
  );
};
